import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { apiClient } from '@/services/api-client';
import type { DeletionRequest } from '@/types';

interface DeleteRequestDialogProps {
  request: DeletionRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

// Recommended hold between archiving and permanent deletion.
const GRACE_PERIOD_DAYS = 7;

function isPreconditionError(code?: string) {
  return code === 'PRECONDITION_FAILED' || code === 'PRECONDITION_FAILED_ERROR';
}

function normalizeEmail(value: string) {
  return value.trim().toLowerCase();
}

export function DeleteRequestDialog({
  request,
  open,
  onOpenChange,
  onSuccess,
}: DeleteRequestDialogProps) {
  const queryClient = useQueryClient();
  const [confirmEmail, setConfirmEmail] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setConfirmEmail('');
      setConfirmError(null);
      setSubmitError(null);
    }
  }, [open, request?.id]);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(
        `/api/v1/admin/deletion-requests/${request!.id}/delete`,
        { confirmEmail }
      );
      if (!response.success) {
        const error = new Error(response.error?.message ?? 'Failed to delete account') as Error & {
          code?: string;
        };
        error.code = response.error?.code;
        throw error;
      }
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'deletion-requests'] });
      // Archive/delete change the underlying user, so the User Management list is stale too.
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: Error & { code?: string }) => {
      if (isPreconditionError(error.code)) {
        setConfirmError('Email did not match');
        return;
      }
      if (error.code === 'FORBIDDEN') {
        setSubmitError('Only a super admin can delete another admin account.');
        return;
      }
      setSubmitError(error.message || 'Something went wrong while deleting the account.');
    },
  });

  const isConfirmValid = useMemo(
    () => !!request && normalizeEmail(confirmEmail) === normalizeEmail(request.userEmail),
    [confirmEmail, request]
  );

  if (!request) return null;

  const isDeleting = deleteMutation.isPending;
  const archivedDaysAgo = request.reviewedAt
    ? Math.floor((Date.now() - new Date(request.reviewedAt).getTime()) / 86_400_000)
    : null;
  const withinGrace = archivedDaysAgo != null && archivedDaysAgo < GRACE_PERIOD_DAYS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Permanently delete account</DialogTitle>
          <DialogDescription>
            This permanently erases all data for {request.userEmail} — sessions, memories, story
            progress, personas — and removes their login. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {archivedDaysAgo != null && (
            <div
              className={cn(
                'rounded-md border px-3 py-2 text-sm',
                withinGrace
                  ? 'border-yellow-500/40 bg-yellow-500/10 text-yellow-700 dark:text-yellow-300'
                  : 'border-border bg-muted/40 text-muted-foreground'
              )}
            >
              Archived {archivedDaysAgo} day{archivedDaysAgo === 1 ? '' : 's'} ago.
              {withinGrace &&
                ` The recommended hold is ${GRACE_PERIOD_DAYS} days before permanent deletion.`}
            </div>
          )}

          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <div className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Type the user's email to confirm:{' '}
                <span className="font-semibold">{request.userEmail}</span>
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-delete-email">User email</Label>
            <Input
              id="confirm-delete-email"
              value={confirmEmail}
              onChange={(event) => {
                setConfirmEmail(event.target.value);
                setConfirmError(null);
                setSubmitError(null);
              }}
              className={cn(confirmError && 'border-destructive focus-visible:ring-destructive')}
              autoComplete="off"
            />
            {confirmError && <p className="text-sm text-destructive">{confirmError}</p>}
          </div>

          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => deleteMutation.mutate()}
            disabled={!isConfirmValid || isDeleting}
          >
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
